/**
 * Especialidades del policlínico — reemplaza la antigua pantalla "Tarifas".
 *
 * Es la fuente única de "qué atiende este policlínico": qué especialidades
 * ofrece, a qué precio la consulta, y qué profesionales las atienden. La
 * pantalla anterior solo dejaba tocar el precio, y encima únicamente de las
 * especialidades que YA tenían profesional — así era imposible definir la
 * oferta antes de contratar, que es el orden real de las cosas.
 *
 * Requiere permiso especialidad:assign.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, AlertTriangle, Check, Plus, Search, X } from "lucide-react";
import {
  type OfertaEspecialidad,
  type ServicioMini,
  asignarProfesional,
  actualizarServicio,
  crearEspecialidadPropia,
  crearServicio,
  estadoDe,
  listOferta,
  quitarProfesional,
  updateOferta,
} from "../api/especialidades";
import { listProfesionales } from "../api/profesionales";
import { formatApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const ESTADO_UI = {
  operativa: { label: "Operativa", variant: "success" as const, ayuda: "" },
  sin_precio: {
    label: "Sin precio",
    variant: "warning" as const,
    ayuda: "La cita se agendará con monto 0 y Caja no sabrá cuánto cobrar.",
  },
  sin_profesional: {
    label: "Sin profesional",
    variant: "warning" as const,
    ayuda: "No se puede agendar: falta asignar un profesional activo.",
  },
  no_ofrecida: { label: "No se ofrece", variant: "neutral" as const, ayuda: "" },
};

export function EspecialidadesPage() {
  const queryClient = useQueryClient();
  const [busqueda, setBusqueda] = useState("");
  const [soloOfrecidas, setSoloOfrecidas] = useState(true);
  const [precios, setPrecios] = useState<Record<number, string>>({});
  const [guardado, setGuardado] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asignando, setAsignando] = useState<number | null>(null);
  const [creandoPropia, setCreandoPropia] = useState(false);
  const [creandoServicio, setCreandoServicio] = useState<OfertaEspecialidad | null>(null);
  const [editandoServicio, setEditandoServicio] = useState<ServicioMini | null>(null);

  const { data: oferta = [], isLoading } = useQuery({
    queryKey: ["oferta-especialidades"],
    queryFn: listOferta,
  });
  const { data: profesionales = [] } = useQuery({
    queryKey: ["profesionales", "all"],
    queryFn: () => listProfesionales(),
    staleTime: 60_000,
  });

  // Toda mutación devuelve la fila consolidada; la parcheamos en la caché para
  // no re-pedir la lista completa en cada clic.
  function aplicar(fila: OfertaEspecialidad) {
    queryClient.setQueryData<OfertaEspecialidad[]>(["oferta-especialidades"], (prev) =>
      (prev ?? []).map((o) => (o.especialidad_id === fila.especialidad_id ? fila : o)),
    );
    // Profesionales cambió de especialidades: su listado quedó viejo.
    queryClient.invalidateQueries({ queryKey: ["profesionales"] });
    setError(null);
  }

  const mutOferta = useMutation({
    mutationFn: ({
      id,
      cambios,
    }: {
      id: number;
      cambios: { activa?: boolean; precio?: number | null; requiere_triaje?: boolean };
    }) =>
      updateOferta(id, cambios),
    onSuccess: (fila, vars) => {
      aplicar(fila);
      if (vars.cambios.precio !== undefined) {
        setGuardado(vars.id);
        setTimeout(() => setGuardado((g) => (g === vars.id ? null : g)), 2000);
      }
    },
    onError: (err) => setError(formatApiError(err, "No se pudo guardar el cambio.")),
  });

  const mutAsignar = useMutation({
    mutationFn: ({ id, profId }: { id: number; profId: number }) => asignarProfesional(id, profId),
    onSuccess: (fila) => {
      aplicar(fila);
      setAsignando(null);
    },
    onError: (err) => setError(formatApiError(err, "No se pudo asignar el profesional.")),
  });

  const mutQuitar = useMutation({
    mutationFn: ({ id, profId }: { id: number; profId: number }) => quitarProfesional(id, profId),
    onSuccess: aplicar,
    onError: (err) => setError(formatApiError(err, "No se pudo quitar el profesional.")),
  });

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return oferta.filter((o) => {
      if (soloOfrecidas && !o.activa) return false;
      if (!q) return true;
      return o.nombre.toLowerCase().includes(q) || o.codigo.toLowerCase().includes(q);
    });
  }, [oferta, busqueda, soloOfrecidas]);

  const ofrecidasTotal = oferta.filter((o) => o.activa).length;
  // Se contaban aparte las "propias" —las que creaba el policlínico— frente a
  // las del catálogo de la plataforma. Esa distinción murió con el
  // multi-tenant: hay una instalación por policlínico y el catálogo es suyo
  // entero, venga de la lista del MINSA o lo haya escrito él.
  //
  // Peor todavía: el contador miraba un `tenant_id` que el servidor ya no
  // manda, así que decía "0 propias" siempre y la etiqueta «Propia» no salía
  // nunca — aunque hubiera especialidades creadas a mano.

  function precioActual(o: OfertaEspecialidad) {
    return precios[o.especialidad_id] ?? (o.precio != null ? String(Number(o.precio)) : "");
  }

  function precioCambió(o: OfertaEspecialidad) {
    const editado = precios[o.especialidad_id];
    if (editado === undefined) return false;
    const original = o.precio != null ? String(Number(o.precio)) : "";
    return editado.trim() !== original;
  }

  function guardarPrecio(o: OfertaEspecialidad) {
    const raw = precioActual(o).trim();
    if (raw === "") {
      mutOferta.mutate({ id: o.especialidad_id, cambios: { precio: null } });
      return;
    }
    const precio = Number(raw);
    if (Number.isNaN(precio) || precio < 0) {
      setError("El precio debe ser un número mayor o igual a 0.");
      return;
    }
    mutOferta.mutate({ id: o.especialidad_id, cambios: { precio } });
  }

  /** Profesionales del tenant que pueden ejercer esta especialidad y aún no la tienen. */
  function elegibles(o: OfertaEspecialidad) {
    const yaAsignados = new Set(o.profesionales.map((p) => p.id));
    return profesionales.filter(
      (p) =>
        !yaAsignados.has(p.id) &&
        p.activo &&
        (o.profesiones.length === 0 ||
          (o.profesiones as string[]).includes(p.tipo_colegio)),
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Especialidades</h1>
        <p className="mt-1 text-sm text-slate-500">
          Qué atiende el policlínico, a qué precio la consulta y qué profesionales la
          cubren. El precio se hereda a la cita al agendar y es lo que Caja cobra.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card>
        <CardHeader>
          <div className="space-y-3">
            {/* Pestañas en vez de un checkbox: los dos números son la
                pregunta real —cuántas ofrezco y cuántas hay disponibles— y
                antes había que descubrir un check para verlos. */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex rounded-lg border border-slate-200 p-0.5">
                <button
                  onClick={() => setSoloOfrecidas(true)}
                  aria-pressed={soloOfrecidas}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                    soloOfrecidas ? "bg-[color:var(--color-link)] text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Las que ofrezco ({ofrecidasTotal})
                </button>
                <button
                  onClick={() => setSoloOfrecidas(false)}
                  aria-pressed={!soloOfrecidas}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                    !soloOfrecidas ? "bg-[color:var(--color-link)] text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Todas las disponibles ({oferta.length})
                </button>
              </div>

              <div className="relative ml-auto">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar especialidad…"
                  aria-label="Buscar especialidad"
                  className="w-60 pl-8"
                />
              </div>

              <Button onClick={() => setCreandoPropia(true)}>
                <Plus className="h-4 w-4" />
                Nueva especialidad
              </Button>
            </div>

            <p className="text-xs text-slate-500">
              {soloOfrecidas ? (
                <>
                  Estas son las que tu policlínico atiende. Para sumar otra, entra en{" "}
                  <strong>Todas las disponibles</strong> y marca su casilla; si no está
                  en la lista, créala con <strong>Nueva especialidad</strong>.
                </>
              ) : (
                <>
                  <strong>{oferta.length}</strong> especialidades en tu catálogo.
                  Marca la casilla de las que quieras ofrecer y ponles precio.
                </>
              )}
            </p>
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}

          {!isLoading && visibles.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              {soloOfrecidas
                ? "No ofreces ninguna especialidad todavía. Desmarca «Solo las que ofrezco» para activarlas."
                : "Ninguna especialidad coincide con la búsqueda."}
            </div>
          )}

          <div className="divide-y divide-slate-100">
            {visibles.map((o) => {
              const estado = estadoDe(o);
              const ui = ESTADO_UI[estado];
              const disponibles = elegibles(o);
              const bloqueada = o.citas_vigentes > 0;

              return (
                <div key={o.especialidad_id} className="px-6 py-4">
                  <div className="flex flex-wrap items-start gap-4">
                    {/* Ofrezco / no ofrezco */}
                    <label className="flex shrink-0 cursor-pointer items-center gap-2 pt-0.5">
                      <input
                        type="checkbox"
                        checked={o.activa}
                        disabled={o.activa && bloqueada}
                        onChange={(e) =>
                          mutOferta.mutate({
                            id: o.especialidad_id,
                            cambios: { activa: e.target.checked },
                          })
                        }
                        className="h-4 w-4 rounded border-slate-300 disabled:opacity-40"
                        aria-label={`Ofrecer ${o.nombre}`}
                        title={
                          o.activa && bloqueada
                            ? `Tiene ${o.citas_vigentes} cita(s) pendientes: atiéndelas o cancélalas antes de dejar de ofrecerla.`
                            : undefined
                        }
                      />
                      <span className="sr-only">Ofrecer {o.nombre}</span>
                    </label>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-sm font-medium ${o.activa ? "text-slate-900" : "text-slate-400"}`}
                        >
                          {o.nombre}
                        </span>
                        <span className="font-mono text-xs text-slate-400">{o.codigo}</span>
                        <Badge variant={ui.variant}>{ui.label}</Badge>

                        {/* Si aquí se toman funciones vitales.

                            Va pegado al nombre y no en una pantalla de ajustes
                            aparte porque es una propiedad de la especialidad,
                            como su precio: Odontología no tría a nadie y quien
                            monta el catálogo lo sabe en ese momento, no dos
                            menús después.

                            Apagado no prohíbe: enfermería puede tomarlo igual
                            si el médico lo pide. Lo que cambia es que deja de
                            bloquear la consulta y de llenarle la bandeja. */}
                        {o.activa && (
                          <button
                            type="button"
                            onClick={() =>
                              mutOferta.mutate({
                                id: o.especialidad_id,
                                cambios: { requiere_triaje: !o.requiere_triaje },
                              })
                            }
                            disabled={mutOferta.isPending}
                            title={
                              o.requiere_triaje
                                ? "Aquí se toman funciones vitales antes de la consulta. Pulsa para dejar de pedirlo."
                                : "Aquí no se toman funciones vitales. Pulsa para volver a pedirlo."
                            }
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition disabled:opacity-50 ${
                              o.requiere_triaje
                                ? "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                                : "border-dashed border-slate-300 bg-slate-50 text-slate-400 hover:border-slate-400"
                            }`}
                          >
                            <Activity className="h-3 w-3" aria-hidden="true" />
                            {o.requiere_triaje ? "Pasa triaje" : "Sin triaje"}
                          </button>
                        )}
                      </div>

                      {o.activa && o.servicios.length > 1 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {o.servicios.map((srv) => (
                            <button
                              key={srv.id}
                              onClick={() => setEditandoServicio(srv)}
                              title="Editar servicio"
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition hover:border-slate-400 ${
                                srv.activo
                                  ? "border-slate-200 bg-white text-slate-700"
                                  : "border-slate-200 bg-slate-50 text-slate-400 line-through"
                              }`}
                            >
                              {srv.es_consulta && <span title="Servicio por defecto">★</span>}
                              {srv.nombre}
                              <span className="font-mono text-slate-500">
                                S/{Number(srv.precio).toFixed(0)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}

                      {ui.ayuda && o.activa && (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          {ui.ayuda}
                        </p>
                      )}

                      {/* Profesionales que la atienden */}
                      {o.activa && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {o.profesionales.map((p) => (
                            <span
                              key={p.id}
                              className={`inline-flex items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-xs ${
                                p.activo
                                  ? "border-slate-200 bg-white text-slate-700"
                                  : "border-slate-200 bg-slate-50 text-slate-400 line-through"
                              }`}
                              title={p.activo ? undefined : "Profesional inactivo"}
                            >
                              {p.nombre_completo}
                              <button
                                onClick={() =>
                                  mutQuitar.mutate({ id: o.especialidad_id, profId: p.id })
                                }
                                disabled={mutQuitar.isPending}
                                className="rounded-full p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                                aria-label={`Quitar a ${p.nombre_completo} de ${o.nombre}`}
                              >
                                <X className="h-3 w-3" aria-hidden="true" />
                              </button>
                            </span>
                          ))}

                          {asignando === o.especialidad_id ? (
                            <select
                              autoFocus
                              defaultValue=""
                              onChange={(e) => {
                                const profId = Number(e.target.value);
                                if (profId) mutAsignar.mutate({ id: o.especialidad_id, profId });
                              }}
                              onBlur={() => setAsignando(null)}
                              aria-label={`Asignar profesional a ${o.nombre}`}
                              className="rounded-lg border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
                            >
                              <option value="">Elegir profesional…</option>
                              {disponibles.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.nombre_completo} ({p.tipo_colegio})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <button
                              onClick={() => setAsignando(o.especialidad_id)}
                              disabled={disponibles.length === 0}
                              className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-0.5 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-40"
                              title={
                                disponibles.length === 0
                                  ? o.profesiones.length > 0
                                    ? `Ningún profesional disponible de ${o.profesiones.join(", ")}.`
                                    : "No hay profesionales activos disponibles."
                                  : undefined
                              }
                            >
                              <Plus className="h-3 w-3" aria-hidden="true" />
                              Asignar
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Servicios: lo que se presta y se cobra bajo esta
                        especialidad. Un policlínico sencillo tiene solo
                        "Consulta" y no necesita mirar aquí. */}
                    {o.activa && (
                      <div className="flex shrink-0 items-center gap-1.5">
                        <span className="text-sm text-slate-400">S/</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={precioActual(o)}
                          onChange={(e) =>
                            setPrecios({ ...precios, [o.especialidad_id]: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && precioCambió(o)) guardarPrecio(o);
                          }}
                          placeholder="—"
                          aria-label={`Precio de consulta de ${o.nombre}`}
                          className="w-28 text-right font-mono"
                        />
                        <button
                          onClick={() => setCreandoServicio(o)}
                          title="Añadir otro servicio a esta especialidad (control, procedimiento…)"
                          className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-[color:var(--color-link)]"
                        >
                          + Servicio
                        </button>
                        <Button
                          variant="ghost"
                          onClick={() => guardarPrecio(o)}
                          disabled={!precioCambió(o) || mutOferta.isPending}
                        >
                          {guardado === o.especialidad_id ? (
                            <>
                              <Check className="h-4 w-4" aria-hidden="true" />
                              Guardado
                            </>
                          ) : (
                            "Guardar"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
      <ServicioDialog
        especialidad={creandoServicio}
        servicio={editandoServicio}
        onClose={() => {
          setCreandoServicio(null);
          setEditandoServicio(null);
        }}
        onGuardado={aplicar}
      />

      <NuevaEspecialidadDialog
        open={creandoPropia}
        onClose={() => setCreandoPropia(false)}
        onCreada={(fila) => {
          queryClient.setQueryData<OfertaEspecialidad[]>(
            ["oferta-especialidades"],
            (prev) => [...(prev ?? []), fila],
          );
          setSoloOfrecidas(true);
          setBusqueda(fila.nombre);
        }}
      />
    </div>
  );
}

const COLEGIOS: { codigo: string; nombre: string }[] = [
  { codigo: "CMP", nombre: "Médico" },
  { codigo: "COP", nombre: "Odontológico" },
  { codigo: "CQFP", nombre: "Químico Farmacéutico" },
  { codigo: "CEP", nombre: "Enfermeros" },
  { codigo: "CObP", nombre: "Obstetras" },
  { codigo: "CTMP", nombre: "Tecnólogo Médico" },
  { codigo: "CPP", nombre: "Psicólogos" },
  { codigo: "CNP", nombre: "Nutricionistas" },
  { codigo: "CBP", nombre: "Biólogos" },
];

/**
 * Crea una especialidad que solo existe para este policlínico.
 *
 * El catálogo de la plataforma (77 códigos MINSA) lo comparten todos, así que
 * no se puede escribir en él desde aquí. Lo que sí puede tener cada policlínico
 * es lo suyo: nadie más lo verá.
 */
function NuevaEspecialidadDialog({
  open,
  onClose,
  onCreada,
}: {
  open: boolean;
  onClose: () => void;
  onCreada: (fila: OfertaEspecialidad) => void;
}) {
  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState("");
  const [codigoTocado, setCodigoTocado] = useState(false);
  const [profesiones, setProfesiones] = useState<string[]>([]);
  const [precio, setPrecio] = useState("");
  const [error, setError] = useState<string | null>(null);

  // El código se propone desde el nombre; sigue siendo editable.
  const codigoFinal = codigoTocado
    ? codigo
    : nombre
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, "")
        .slice(0, 8);

  const crear = useMutation({
    mutationFn: () =>
      crearEspecialidadPropia({
        nombre: nombre.trim(),
        codigo: codigoFinal,
        profesiones,
        precio: precio.trim() === "" ? null : Number(precio),
      }),
    onSuccess: (fila) => {
      onCreada(fila);
      setNombre("");
      setCodigo("");
      setCodigoTocado(false);
      setProfesiones([]);
      setPrecio("");
      setError(null);
      onClose();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo crear la especialidad.")),
  });

  const valido =
    nombre.trim().length >= 2 &&
    /^[A-Za-z0-9-]{2,20}$/.test(codigoFinal) &&
    profesiones.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nueva especialidad"
      description="Solo para tu policlínico. No se añade al catálogo de la plataforma ni la verán otros."
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Nombre" htmlFor="ne_nom">
              <Input
                id="ne_nom"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Terapia de Lenguaje"
              />
            </Field>
          </div>
          <Field label="Código" htmlFor="ne_cod" hint="Corto, sin espacios.">
            <Input
              id="ne_cod"
              value={codigoFinal}
              onChange={(e) => {
                setCodigoTocado(true);
                setCodigo(e.target.value.toUpperCase());
              }}
              className="font-mono"
            />
          </Field>
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700">
            ¿Qué colegios pueden ejercerla?
          </p>
          <p className="mb-2 text-xs text-slate-500">
            El alta de profesionales filtra por esto: sin ningún colegio marcado no
            podrías asignársela a nadie.
          </p>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {COLEGIOS.map((c) => {
              const marcado = profesiones.includes(c.codigo);
              return (
                <button
                  key={c.codigo}
                  type="button"
                  onClick={() =>
                    setProfesiones((prev) =>
                      prev.includes(c.codigo)
                        ? prev.filter((x) => x !== c.codigo)
                        : [...prev, c.codigo],
                    )
                  }
                  aria-pressed={marcado}
                  className={`rounded-lg border px-2.5 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                    marcado ? "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="block font-mono text-xs font-medium text-slate-800">
                    {c.codigo}
                  </span>
                  <span className="block text-[11px] text-slate-500">{c.nombre}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Field
          label="Precio de consulta (S/)"
          htmlFor="ne_precio"
          hint="Opcional; puedes ponerlo después."
        >
          <Input
            id="ne_precio"
            type="number"
            min="0"
            step="0.01"
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
            className="w-40 text-right font-mono"
            placeholder="—"
          />
        </Field>

        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Nace ya marcada como ofrecida. Después podrás asignarle profesionales
          desde esta misma pantalla.
        </p>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={!valido || crear.isPending}>
            Crear especialidad
          </Button>
        </div>
      </div>
    </Dialog>
  );
}


/**
 * Alta y edición de un servicio.
 *
 * Un servicio es lo que se presta y se cobra: "Consulta", "Control",
 * "Papanicolau", "Ecografía". Antes solo había un precio por especialidad, así
 * que el mismo obstetra cobraba lo mismo por una consulta que por una
 * ecografía, o había que teclear el monto a mano en cada cita y entonces la
 * lista de precios no vivía en ninguna parte.
 */
function ServicioDialog({
  especialidad,
  servicio,
  onClose,
  onGuardado,
}: {
  especialidad: OfertaEspecialidad | null;
  servicio: ServicioMini | null;
  onClose: () => void;
  onGuardado: (fila: OfertaEspecialidad) => void;
}) {
  const esEdicion = servicio != null;
  const abierto = especialidad != null || servicio != null;

  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState("");
  const [precio, setPrecio] = useState("");
  const [esConsulta, setEsConsulta] = useState(false);
  const [activo, setActivo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cargado, setCargado] = useState<string | null>(null);

  const clave = esEdicion ? `s${servicio.id}` : `e${especialidad?.especialidad_id}`;
  if (abierto && cargado !== clave) {
    setCargado(clave);
    setNombre(servicio?.nombre ?? "");
    setCodigo(servicio?.codigo ?? "");
    setPrecio(servicio ? String(Number(servicio.precio)) : "");
    setEsConsulta(servicio?.es_consulta ?? false);
    setActivo(servicio?.activo ?? true);
    setError(null);
  }
  if (!abierto && cargado !== null) setCargado(null);

  const guardar = useMutation({
    mutationFn: () => {
      if (esEdicion) {
        return actualizarServicio(servicio.id, {
          nombre: nombre.trim(),
          precio: Number(precio),
          es_consulta: esConsulta,
          activo,
        });
      }
      return crearServicio({
        especialidad_id: especialidad!.especialidad_id,
        nombre: nombre.trim(),
        codigo: codigo.trim().toUpperCase(),
        precio: Number(precio),
        es_consulta: esConsulta,
      });
    },
    onSuccess: (fila) => {
      onGuardado(fila);
      onClose();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo guardar el servicio.")),
  });

  const valido =
    nombre.trim().length >= 2 &&
    (esEdicion || /^[A-Za-z0-9-]{2,30}$/.test(codigo.trim())) &&
    precio.trim() !== "" &&
    Number(precio) >= 0;

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      title={esEdicion ? "Editar servicio" : "Nuevo servicio"}
      description={
        esEdicion
          ? "Los cambios no afectan a las citas ya agendadas: su monto quedó congelado."
          : `Lo que se presta y se cobra en ${especialidad?.nombre ?? ""}.`
      }
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Nombre" htmlFor="sv_nom">
              <Input
                id="sv_nom"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ecografía obstétrica"
              />
            </Field>
          </div>
          <Field
            label="Código"
            htmlFor="sv_cod"
            hint={esEdicion ? "No se cambia." : "Único en tu policlínico."}
          >
            <Input
              id="sv_cod"
              value={codigo}
              disabled={esEdicion}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              className="font-mono"
              placeholder="ECO-OBS"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Precio (S/)" htmlFor="sv_precio">
            <Input
              id="sv_precio"
              type="number"
              min="0"
              step="0.01"
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              className="text-right font-mono"
            />
          </Field>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={esConsulta}
            onChange={(e) => setEsConsulta(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300"
          />
          <span>
            Es la consulta por defecto
            <span className="block text-xs text-slate-500">
              Se cobra cuando se agenda sin elegir servicio. Solo uno por
              especialidad: marcarlo aquí desmarca al anterior.
            </span>
          </span>
        </label>

        {esEdicion && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Activo — desmárcalo para dejar de ofrecerlo sin borrar el histórico
          </label>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={!valido || guardar.isPending}>
            {esEdicion ? "Guardar cambios" : "Crear servicio"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}